import { useMemo, useRef, useState } from 'react';
import styles from './HelpCenterPage.module.css';

type HelpCategory = 'start' | 'threads' | 'collab' | 'email' | 'training' | 'admin' | 'browser' | 'audit';
type HelpStatus = 'ready' | 'needed';

interface HelpVideo {
  id: string;
  title: string;
  category: HelpCategory;
  status: HelpStatus;
  duration: string;
  summary: string;
  src?: string;
  captions?: string;
  tags: string[];
  steps: string[];
}

const categories: Array<{ id: HelpCategory | 'all'; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'start', label: 'Start' },
  { id: 'threads', label: 'Threads' },
  { id: 'collab', label: 'Collaboration' },
  { id: 'email', label: 'Email' },
  { id: 'training', label: 'Training' },
  { id: 'admin', label: 'Admin' },
  { id: 'browser', label: 'Browser Use' },
  { id: 'audit', label: 'Audit' },
];

const videos: HelpVideo[] = [
  {
    id: '01-welcome-tour',
    title: 'Welcome Tour',
    category: 'start',
    status: 'ready',
    duration: '0:34',
    summary: 'Open Foreman, start a thread, and send the first request.',
    src: '/help/videos/01-welcome-tour.mp4',
    captions: '/help/captions/01-welcome-tour.vtt',
    tags: ['home', 'chat', 'threads', 'new users'],
    steps: ['Sign in with your Foreman account.', 'Open Chat from the top bar.', 'Use a new or existing thread for the job question.'],
  },
  {
    id: '02-switching-roles',
    title: 'Switching Roles',
    category: 'start',
    status: 'ready',
    duration: '0:35',
    summary: 'Change the role lens so answers match field, design, accounting, or admin work.',
    src: '/help/videos/02-switching-roles.mp4',
    captions: '/help/captions/02-switching-roles.vtt',
    tags: ['roles', 'field', 'designer', 'accounting'],
    steps: ['Use the role switcher in the top bar.', 'Pick the role that matches the work you are doing.', 'Ask the same thread a follow-up with the new role active.'],
  },
  {
    id: '03-mentions-and-share',
    title: 'Mentions and Sharing',
    category: 'collab',
    status: 'ready',
    duration: '0:44',
    summary: 'Bring a teammate into a thread and keep the job context in one place.',
    src: '/help/videos/03-mentions-and-share.mp4',
    captions: '/help/captions/03-mentions-and-share.vtt',
    tags: ['mentions', 'sharing', 'threads', 'team'],
    steps: ['Type @ and choose the teammate.', 'Send the message in the job thread.', 'Use Share when you need broader access to the thread.'],
  },
  {
    id: '04-email-foreman',
    title: 'Email Foreman Anything',
    category: 'email',
    status: 'ready',
    duration: '0:35',
    summary: 'Forward job emails and files into Foreman so the conversation stays searchable.',
    src: '/help/videos/04-email-foreman.mp4',
    captions: '/help/captions/04-email-foreman.vtt',
    tags: ['email', 'attachments', 'search', 'job files'],
    steps: ['Forward the email or document to Foreman.', 'Open the related thread after it appears.', 'Ask for a summary, comparison, or next action.'],
  },
  {
    id: '05-admin-onboarding',
    title: 'Admin User Onboarding',
    category: 'admin',
    status: 'ready',
    duration: '0:39',
    summary: 'Add users and assign the right access without leaving the app.',
    src: '/help/videos/05-admin-onboarding.mp4',
    captions: '/help/captions/05-admin-onboarding.vtt',
    tags: ['admin', 'users', 'onboarding', 'access'],
    steps: ['Open Users from the top bar.', 'Add the user details and role.', 'Confirm access before sharing customer or job threads.'],
  },
  {
    id: '06-audit-vagheei-estimate',
    title: 'Audit: Vagheei SOW to Estimate',
    category: 'audit',
    status: 'ready',
    duration: '1:04',
    summary: 'Upload the SOW and estimate, then ask Foreman to verify scope, pricing, and omissions.',
    src: '/help/videos/06-audit-vagheei-estimate.mp4',
    captions: '/help/captions/06-audit-vagheei-estimate.vtt',
    tags: ['audit', 'estimate', 'sow', 'vagheei', 'pdf'],
    steps: ['Attach the SOW and estimate to the thread.', 'Ask for a scoped estimate audit.', 'Review the flagged gaps before approving the estimate.'],
  },
  {
    id: '07-review-nauman-sow',
    title: 'Review: SOW and Change Orders',
    category: 'audit',
    status: 'ready',
    duration: '1:01',
    summary: 'Compare the SOW, estimate, and change orders so review notes are concrete.',
    src: '/help/videos/07-review-nauman-sow.mp4',
    captions: '/help/captions/07-review-nauman-sow.vtt',
    tags: ['review', 'change orders', 'sow', 'estimate', 'pricing'],
    steps: ['Attach the SOW, estimate, and change order spreadsheet.', 'Ask Foreman to reconcile the documents.', 'Use the response to decide what needs customer or PM review.'],
  },
  {
    id: '08-thread-search-rename',
    title: 'Search and Rename Threads',
    category: 'threads',
    status: 'ready',
    duration: '0:30',
    summary: 'Find older job conversations by name, email subject, or email body, then rename a thread.',
    src: '/help/videos/08-thread-search-rename.webm',
    captions: '/help/captions/08-thread-search-rename.vtt',
    tags: ['thread search', 'rename', 'email search', 'organization'],
    steps: ['Open Chat and use Search threads with at least two characters.', 'Show Name, Email subject, and Email body matches.', 'Open a result, then rename an owned thread from the pencil or three-dot menu.'],
  },
  {
    id: '09-browser-use-key',
    title: 'Set Your Browser Use Key',
    category: 'browser',
    status: 'ready',
    duration: '0:32',
    summary: 'Add, replace, and clear a personal Browser Use API key from Settings.',
    src: '/help/videos/09-browser-use-key.webm',
    captions: '/help/captions/09-browser-use-key.vtt',
    tags: ['browser use', 'settings', 'api key', 'automation'],
    steps: ['Open Settings from the top bar.', 'Create a Browser Use key at cloud.browser-use.com.', 'Paste the bu_ key, save it, and confirm the masked last four characters.'],
  },
  {
    id: '10-admin-browser-use-invites',
    title: 'Admin: Browser Use and Invites',
    category: 'admin',
    status: 'ready',
    duration: '0:33',
    summary: 'Use the org fallback key, per-user Browser Use keys, and copy/email invite controls.',
    src: '/help/videos/10-admin-browser-use-invites.webm',
    captions: '/help/captions/10-admin-browser-use-invites.vtt',
    tags: ['admin', 'browser use', 'invite email', 'users', 'fallback key'],
    steps: ['Open Users and review the org-wide Browser Use fallback key.', 'Set or clear a user Browser Use key from the table.', 'Copy or email an invite link after creating or updating a user.'],
  },
  {
    id: '11-training-page-workflow',
    title: 'Teach Foreman in Training',
    category: 'training',
    status: 'ready',
    duration: '0:33',
    summary: 'Use corrections, practices, products, waste factors, trends, and bulk import on the Training page.',
    src: '/help/videos/11-training-page-workflow.webm',
    captions: '/help/captions/11-training-page-workflow.vtt',
    tags: ['training', 'corrections', 'practices', 'products', 'waste factors', 'bulk import'],
    steps: ['Open Training and review correction history.', 'Resolve or use corrections to create practices.', 'Add products, waste factors, and bulk-import rows where appropriate.'],
  },
];

const guideSections = [
  {
    title: 'Daily Chat Flow',
    items: ['Use one thread per job or focused issue.', 'Attach the source files before asking for comparisons.', 'Keep follow-ups in the same thread so Foreman retains context.'],
  },
  {
    title: 'Search and Organize',
    items: ['Type two or more characters in Search threads for server-side results.', 'Admins see all readable threads; other users see threads they can access.', 'Rename owned threads so job names are clear later.'],
  },
  {
    title: 'Browser Use Setup',
    items: ['Open Settings to save a personal Browser Use API key.', 'Admins can set an org-wide fallback key and per-user keys from Users.', 'Clear keys when access changes or an account should stop using Browser Use.'],
  },
  {
    title: 'Audit Workflow',
    items: ['Start with the SOW and estimate.', 'Add change orders when the job scope has moved.', 'Ask for missing scope, pricing mismatches, allowances, and owner-facing questions.'],
  },
  {
    title: 'Team Workflow',
    items: ['Mention teammates when they need to review a live thread.', 'Share only the threads needed for the job.', 'Use Training for corrections that should improve future answers.'],
  },
];

function matchesVideo(video: HelpVideo, query: string) {
  const haystack = [video.title, video.summary, video.category, video.status, ...video.tags, ...video.steps].join(' ').toLowerCase();
  return haystack.includes(query.toLowerCase().trim());
}

function mediaType(src: string) {
  return src.endsWith('.webm') ? 'video/webm' : 'video/mp4';
}

export default function HelpCenterPage() {
  const [activeCategory, setActiveCategory] = useState<HelpCategory | 'all'>('all');
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState(videos[5].id);
  const playerRef = useRef<HTMLElement | null>(null);

  const filteredVideos = useMemo(() => {
    return videos.filter((video) => {
      const categoryMatch = activeCategory === 'all' || video.category === activeCategory;
      const queryMatch = query.trim().length === 0 || matchesVideo(video, query);
      return categoryMatch && queryMatch;
    });
  }, [activeCategory, query]);

  const selectedVideo = filteredVideos.find((video) => video.id === selectedId)
    ?? filteredVideos[0]
    ?? videos.find((video) => video.id === selectedId)
    ?? videos[0];

  const handleSelectVideo = (id: string) => {
    setSelectedId(id);
    if (window.matchMedia('(max-width: 1100px)').matches) {
      requestAnimationFrame(() => {
        playerRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
      });
    }
  };

  return (
    <main className={styles.page}>
      <section className={styles.library}>
        <div className={styles.libraryHeader}>
          <div>
            <p className={styles.eyebrow}>Help Center</p>
            <h1>Guide and Video Library</h1>
          </div>
          <div className={styles.searchWrap}>
            <label className={styles.searchLabel} htmlFor="help-search">Search</label>
            <input
              id="help-search"
              className={styles.search}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search videos, steps, or job workflows"
            />
          </div>
        </div>

        <div className={styles.categoryRow} aria-label="Help categories">
          {categories.map((category) => (
            <button
              key={category.id}
              type="button"
              className={`${styles.categoryButton} ${activeCategory === category.id ? styles.categoryButtonActive : ''}`}
              onClick={() => setActiveCategory(category.id)}
            >
              {category.label}
            </button>
          ))}
        </div>

        <div className={styles.contentGrid}>
          <section ref={playerRef} className={styles.playerPanel} aria-label="Selected training video">
            {selectedVideo.src ? (
              <video
                key={selectedVideo.id}
                className={styles.videoPlayer}
                controls
                preload="metadata"
              >
                <source src={selectedVideo.src} type={mediaType(selectedVideo.src)} />
                {selectedVideo.captions && (
                  <track kind="captions" src={selectedVideo.captions} srcLang="en" label="English" />
                )}
              </video>
            ) : (
              <div className={styles.neededPanel}>
                <span className={styles.neededKicker}>Recording needed</span>
                <h2>{selectedVideo.title}</h2>
                <p>{selectedVideo.summary}</p>
              </div>
            )}
            <div className={styles.videoMeta}>
              <div>
                <p className={styles.duration}>
                  {selectedVideo.status === 'needed' ? 'Needed video' : selectedVideo.duration}
                </p>
                <h2>{selectedVideo.title}</h2>
                <p>{selectedVideo.summary}</p>
              </div>
              <div className={styles.stepList}>
                {selectedVideo.steps.map((step) => (
                  <span key={step}>{step}</span>
                ))}
              </div>
            </div>
          </section>

          <aside className={styles.videoList} aria-label="Help videos">
            {filteredVideos.map((video) => (
              <button
                key={video.id}
                type="button"
                className={`${styles.videoCard} ${selectedVideo.id === video.id ? styles.videoCardActive : ''}`}
                onClick={() => handleSelectVideo(video.id)}
              >
                <span className={styles.cardTitle}>{video.title}</span>
                <span className={styles.cardSummary}>{video.summary}</span>
                <span className={styles.cardFooter}>
                  <span>{video.duration}</span>
                  <span>
                    {video.status === 'needed' ? 'Needed' : categories.find((category) => category.id === video.category)?.label}
                  </span>
                </span>
              </button>
            ))}
            {filteredVideos.length === 0 && (
              <div className={styles.emptyState}>No videos match that search.</div>
            )}
          </aside>
        </div>
      </section>

      <section className={styles.guideGrid} aria-label="Written guide">
        {guideSections.map((section) => (
          <article className={styles.guidePanel} key={section.title}>
            <h2>{section.title}</h2>
            <ul>
              {section.items.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </article>
        ))}
      </section>
    </main>
  );
}
